// Camera captures on the kiosk (UC-PATIENTS-023 step 1 and 2; PAT-26; BR-PATIENTS-012): the ID document, the patient's photo
// and the medical-aid card, front and back. A photo from a tablet camera is 3–8 MB; the document limit is 2 MB and 2 000 px on
// the longer side, so the image is scaled and re-encoded as JPEG on the device before it travels inline in the submission
// (InlineUpload: base64 without a data-URL prefix). Nothing here touches the network.
import { MAX_UPLOAD_BYTES, MAX_UPLOAD_PX, base64DecodedBytes, type InlineUpload } from "@visionx/contracts";

export interface Capture {
  upload: InlineUpload;
  /** When the photo was taken on the device (ISO instant), for the "captured 08:19" caption. */
  capturedAt: string;
  /** A data URL for the tile preview. */
  previewUrl: string;
}

const QUALITIES = [0.85, 0.75, 0.65, 0.55, 0.45] as const;

async function decode(file: Blob): Promise<{ width: number; height: number; draw: (ctx: CanvasRenderingContext2D, w: number, h: number) => void; close: () => void }> {
  if (typeof createImageBitmap === "function") {
    const bmp = await createImageBitmap(file);
    return { width: bmp.width, height: bmp.height, draw: (ctx, w, h) => ctx.drawImage(bmp, 0, 0, w, h), close: () => bmp.close() };
  }
  const url = URL.createObjectURL(file);
  const img = await new Promise<HTMLImageElement>((resolve, reject) => {
    const el = new Image();
    el.onload = () => resolve(el);
    el.onerror = () => reject(new Error("The photo could not be read."));
    el.src = url;
  });
  return { width: img.naturalWidth, height: img.naturalHeight, draw: (ctx, w, h) => ctx.drawImage(img, 0, 0, w, h), close: () => URL.revokeObjectURL(url) };
}

/** Scales a captured image to the document limits and encodes it as a JPEG inline upload. Throws when it cannot be brought under 2 MB. */
export async function compressImage(file: Blob, now: Date = new Date()): Promise<Capture> {
  const src = await decode(file);
  try {
    const scale = Math.min(1, MAX_UPLOAD_PX / Math.max(src.width, src.height));
    const w = Math.max(1, Math.round(src.width * scale));
    const h = Math.max(1, Math.round(src.height * scale));
    const canvas = document.createElement("canvas");
    canvas.width = w;
    canvas.height = h;
    const ctx = canvas.getContext("2d");
    if (!ctx) throw new Error("The photo could not be processed on this device.");
    src.draw(ctx, w, h);
    for (const q of QUALITIES) {
      const dataUrl = canvas.toDataURL("image/jpeg", q);
      const data = dataUrl.slice(dataUrl.indexOf(",") + 1);
      if (base64DecodedBytes(data) <= MAX_UPLOAD_BYTES) {
        return { upload: { contentType: "image/jpeg", data }, capturedAt: now.toISOString(), previewUrl: dataUrl };
      }
    }
    throw new Error("The photo is too large even after compression. Try again from a little further away.");
  } finally {
    src.close();
  }
}
